import type { ApiClient } from '../generated/laravel-typed-openapi';

/**
 * The time zone list over REST — `GET /timezones`.
 *
 * Replaces the Lumen `getTimeZones` query. The whole table comes back in one response, ordered by
 * the backend, which is what lets the pickers search a list they already hold instead of asking
 * per keystroke.
 *
 * The response is read defensively. Scramble infers response types from the controller's return
 * type and declares this one a bare `string`, so the declared type says nothing about the payload
 * — the rows are parsed at runtime instead, accepting the list either bare or wrapped in the
 * `data` envelope Laravel resources use. A payload in neither shape yields an empty list, which
 * leaves the pickers on their bundled copy rather than crashing them.
 */

/** A zone as the pickers use it: the id as text, which is what every form value on both apps holds. */
export type TimezoneOption = { id: string; name: string };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Tolerates a JSON body arriving as a string, which is what this response is declared to be. */
const parsePayload = (payload: unknown): unknown => {
  if (typeof payload !== 'string') {
    return payload;
  }

  try {
    return JSON.parse(payload);
  } catch {
    return undefined;
  }
};

const readRows = (payload: unknown): unknown[] => {
  const parsed = parsePayload(payload);

  if (Array.isArray(parsed)) {
    return parsed;
  }

  return isRecord(parsed) && Array.isArray(parsed.data) ? parsed.data : [];
};

/** Rows without both an id and a name are dropped: neither the picker nor a write can use them. */
const toOption = (row: unknown): TimezoneOption | undefined => {
  if (!isRecord(row)) {
    return undefined;
  }

  const { id, name } = row;
  const hasId = typeof id === 'string' || typeof id === 'number';

  return hasId && typeof name === 'string' ? { id: String(id), name } : undefined;
};

export const getTimezones = async (apiClient: ApiClient): Promise<TimezoneOption[]> => {
  const payload = await apiClient.get('/timezones');

  return readRows(payload).flatMap((row) => toOption(row) ?? []);
};
